import React, { useState, useEffect } from "react";
import { useChartData } from "./useChartData";
import styles from "../Chart.module.css";
import { Row, Container, Col } from "reactstrap";
import FvgInfo from "../Info/FvgInfo"; // Import the FvgInfo component
import { FaInfoCircle } from "react-icons/fa";
import { useAppContext } from "../../../App"; // Import AppContext
import NewsAndSentiment from "../../News/NewsAndSentiment";
import NewsAndSentiment1 from "../../News/NewsAndSentiment1";
import CalendarEventData from "../../News/displayCalendarEvents";
import CftcDataTable from "../../News/cftc"
import CftcDataChart from "../../News/cftcChart";
import { FaPlus, FaMinus } from "react-icons/fa";

const FVGChart = () => {
  const {
    currencyPair, setCurrencyPair, seriesId, setSeriesId, timeInterval, setTimeInterval, timeZone, setTimeZone, timeZones, g50Currencies, timeIntervals, Plot, LoadingSpinner, Dropdown, dateRange, setDateRange, handleRelayout, availableDates, setAvailableDates, currentPrice, setCurrentPrice
  } = useAppContext(); // Destructure values from AppContext

  const [gapThreshold, setGapThreshold] = useState(0.0001);
  const [lsGapThreshold, setLSGapThreshold] = useState(0.0001);
  const [maxDifference, setMaxDifference] = useState(0.2); // Default max
  const [lsMaxDifference, setLSMaxDifference] = useState(0.2); // Default max
  const [cacheLevel, setCacheLevel] = useState(false); // Default max
  const [expanded, setExpanded] = useState(false);
  const [specExpanded, setSpecExpanded] = useState(false);
  const [calendarEventExpanded, setCalendarEventExpanded] = useState(false);
  const [showFvgData, setShowFvgData] = useState(true);
  const [showOrderBook, setShowOrderBook] = useState(false);
  const [showLiquiditySweep, setShowLiquiditySweep] = useState(false);
  const [orderBookExpanded, setOrderBookExpanded] = useState(true);
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");

  // Risk/Reward ratio: 1:x
  // Let’s store just the "x" part in state, for example
  const [lossRatio, setLossRatio] = useState(1);
  const [gainRatio, setGainRatio] = useState(2);


  // SET VALUES OF THE ORDERBOOK
  const [orderBookImbalance, setOrderBookImbalance] = useState(1.5);
  const [orderBookRange, setOrderBookRange] = useState(1.0);
  const [orderBookCandleRange, setOrderBookCandleRange] = useState(10);

  const handleOrderBookImbalanceChange = (e) => {
    setOrderBookImbalance(e.target.value);
  };

  const handleOrderBookRangeChange = (e) => {
    setOrderBookRange(e.target.value);
  };

  const handleOrderBookCandleRangeChange = (e) => {
    setOrderBookCandleRange(e.target.value);
  };

  const handleLossRatioSliderChange = (e) => {
    setLossRatio(e.target.value);
  };

  const handleGainRatioSliderChange = (e) => {
    setGainRatio(e.target.value);
  };
  const toggleOrderBook = () => {
    setShowOrderBook(prev => !prev);
  };

  const toggleFvgData = () => {
    setShowFvgData(prev => !prev);
  };

  // Track window width
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Decide font size based on screen width
  const legendFontSize = windowWidth < 768 ? 7 : 14;
  const legendFontPosition = windowWidth < 768 ? 100 : 40;

  const toggleExpand = () => {
    setExpanded((prev) => !prev);
  };


  const toggleSpecExpand = () => {
    setSpecExpanded((prev) => !prev);
  };


  const toggleLiquiditySweep = () => {
    setShowLiquiditySweep((prev) => !prev);
  };

  const toggleCalendarEventExpand = () => {
    setCalendarEventExpanded((prev) => !prev);
  };

  const [isInfoOpen, setIsInfoOpen] = useState(false); // Manage FvgInfo state
  const toggleInfoDrawer = () => {
    setIsInfoOpen((prev) => !prev); // Toggle FvgInfo drawer
  };

  const { chartData, loading, error } = useChartData(currencyPair, timeInterval, timeZone, gapThreshold, lsGapThreshold, setMaxDifference, setLSMaxDifference, cacheLevel, setCacheLevel, dateRange, setDateRange, availableDates, setAvailableDates, currentPrice, setCurrentPrice, lossRatio, gainRatio, orderBookImbalance, orderBookRange, orderBookCandleRange, showOrderBook, showFvgData, seriesId, showLiquiditySweep);

  // We store the array of traces in local state so we can modify them
  const [plotTraces, setPlotTraces] = useState([]);

  useEffect(() => {
    if (chartData?.traces) {
      setPlotTraces(chartData.traces);
    }
  }, [chartData]);

  // USER CLICKS LEGEND => Toggle lines
  const handleLegendClick = (event) => {
    const clickedTraceIndex = event.curveNumber;
    const linesForMarker = chartData?.markerToLinesMap?.[clickedTraceIndex];
    if (!linesForMarker) {
      // Return nothing → Plotly does normal toggling of the clicked trace
      return;
    }

    // Toggle lines
    setPlotTraces((prev) => {
      const newTraces = [...prev];
      linesForMarker.forEach((lineIndex) => {
        const isVisible = newTraces[lineIndex].visible !== "legendonly";
        newTraces[lineIndex] = {
          ...newTraces[lineIndex],
          visible: isVisible ? "legendonly" : true,
        };
      });
      return newTraces;
    });

    // If you do NOT return false, Plotly will still hide/show the clicked marker trace
    // return; // or return true; either way, it does NOT prevent default toggling.
  };

  const handleCurrencyPairChange = (value) => {
    //setLocalCurrencyPair(value);
    setCurrencyPair(value); // Update global state
    // Find the series ID for the selected currency pair and set it
    const selectedCurrency = g50Currencies.find((currency) => currency.pair === value);
    setSeriesId(selectedCurrency?.seriesId || null);
    setGapThreshold(0.0001)
    setLSGapThreshold(0.0001)
    setCacheLevel(true)
  };

  const handleTimeIntervalChange = (value) => {
    //setLocalTimeInterval(value);
    setTimeInterval(value); // Update global state
  };

  const handleTimeZoneChange = (value) => {
    //setLocalTimeZone(value);
    setTimeZone(value); // Update global state
  };

  const handleGapThresholdChange = (e) => {
    setGapThreshold(parseFloat(e.target.value));
  };
  const handleLSGapThresholdChange = (e) => {
    setLSGapThreshold(parseFloat(e.target.value));
  };

  if (loading) {
    return <LoadingSpinner message="Loading chart data..." />;
  }

  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  }

  return (
    <Container>

      <Row>
        <Col md={8}>
          <Row>
            <Col xs={12} md={12}>
              <div>
                {/* Header with Info Icon */}
                <div className={styles.header}>
                  <h2 className={styles.chartTitle}>
                    Fair Value Gap Analysis - {currencyPair} {currentPrice}
                    <FaInfoCircle
                      onClick={toggleInfoDrawer}
                      style={{
                        marginLeft: "20px",
                        cursor: "pointer",
                        fontSize: "20px",
                        verticalAlign: "middle",
                      }}
                      title="Open FVG Info" // Tooltip for accessibility
                    />
                  </h2>
                </div>
                {/* FvgInfo Drawer */}
                <FvgInfo isOpen={isInfoOpen} toggleDrawer={toggleInfoDrawer} />
              </div>
            </Col>
          </Row>
          <Row>
            {/* Dropdowns */}
            <Col xs={4} md={4}>
             
              <Dropdown
                label={`Currency`}
                options={g50Currencies.map((currency) => ({
                  label: `${currency.country} (${currency.pair})`,
                  value: currency.pair,
                }))}
                value={currencyPair}
                onChange={handleCurrencyPairChange}
              />
            </Col>
            <Col xs={4} md={4}>
              <Dropdown
                label="Time Interval"
                options={timeIntervals}
                value={timeInterval}
                onChange={handleTimeIntervalChange}
              />
            </Col>
            <Col xs={4} md={4}>
              <Dropdown
                label="Time Zone"
                options={timeZones.map((zone) => ({
                  label: zone.label,
                  value: zone.value,
                }))}
                value={timeZone}
                onChange={handleTimeZoneChange}
              />
            </Col>
          </Row>

          <Row>
          <div
                onClick={toggleSpecExpand}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  background: "#f1f1f1",
                  padding: "0.5rem",
                  borderRadius: "4px",
                }}
              >
                {specExpanded ? <FaMinus /> : <FaPlus />}
                <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                  {specExpanded ? "Hide Currency Pair Settings" : "Show Currency Pair Settings"}
                </span>
              </div>
          </Row>

          {specExpanded && (
            <>
              <Row>
                {/* Conditional Rendering: Display seriesId, Start Date, and End Date Pickers */}
                {g50Currencies.some((currency) => currency.pair === currencyPair && currency.seriesId) && (
                  <>
                    <span className={styles.settingsTitle}>Currency Pair  Settings
                    </span>
                    <Col xs={12} md={4}>
                      <div>
                        <label className={styles.sliderLabel}> Series ID</label>
                        <p>{g50Currencies.find((currency) => currency.pair === currencyPair)?.seriesId}</p>
                      </div>
                    </Col>
                    <Col xs={6} md={4}>
                      <div>
                        <label className={styles.sliderLabel}>Start Date</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="form-control"
                        />
                      </div>
                    </Col>
                    <Col xs={6} md={4}>
                      <div>
                        <label className={styles.sliderLabel}>End Date</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="form-control"
                        />
                      </div>
                    </Col>
                  </>
                )}
              </Row>

              <Row>
                <Col xs={2} md={4}>
                  <div className={styles.sliderContainer}>
                    <label htmlFor="lossRation" className={styles.sliderLabel}>
                      Loss Ratio
                    </label>
                  </div>
                </Col>
                <Col xs={2} md={4}>
                  <div className={styles.sliderContainer}>
                    <label htmlFor="gainRatio" className={styles.sliderLabel}>
                      Gain Ratio
                    </label>
                  </div>
                </Col>
                <Col xs={4} md={4}>
                  {availableDates.start && availableDates.end && (
                    <div className={styles.sliderContainer}>
                      <label htmlFor="dateRangeSlider" className={styles.sliderLabel}>
                        Available Date Range: {new Date(availableDates.start).toLocaleDateString()} -{" "}
                        {new Date(availableDates.end).toLocaleDateString()}
                      </label>
                    </div>
                  )}
                </Col>
              </Row>
              <Row>
                <Col xs={2} md={4}>
                  <div className={styles.sliderContainer}>
                    <input
                      type="range"
                      id="rrSlider"
                      min="1"
                      max="10"
                      step="1"
                      value={lossRatio}
                      onChange={handleLossRatioSliderChange}
                      className={styles.slider}
                    />
                  </div>
                </Col>
                <Col xs={2} md={4}>
                  <div className={styles.sliderContainer}>
                    <input
                      type="range"
                      id="rrSlider"
                      min="1"
                      max="10"
                      step="1"
                      value={gainRatio}
                      onChange={handleGainRatioSliderChange}
                      className={styles.slider}
                    />
                  </div>
                </Col>
                <Col xs={4} md={4}>
                  {availableDates.start && availableDates.end && (
                    <div className={styles.sliderContainer}>
                      <input
                        type="range"
                        id="dateRangeSlider"
                        min={new Date(availableDates.start).getTime()} // Minimum is the start of available dates
                        max={new Date(availableDates.end).getTime()} // Maximum is the end of available dates
                        step={3600000} // 1 day in milliseconds
                        value={new Date(dateRange.start).getTime()} // Bind slider to the start date
                        onChange={(e) => {
                          const start = new Date(parseInt(e.target.value)).toISOString(); // Parse slider value to ISO format
                          setDateRange((prev) => ({ ...prev, start })); // Update the start date
                        }}
                        className={styles.slider}
                      />
                    </div>
                  )}
                </Col>
              </Row>
              <Row>

                <Col xs={2} md={4}>
                  <div className={styles.sliderContainer}>
                    <label htmlFor="gapThreshold" className={styles.sliderLabel}>
                      Loss Ratio: <strong>{lossRatio}</strong>
                    </label>
                  </div>
                </Col>
                <Col xs={2} md={4}>
                  <div className={styles.sliderContainer}>
                    <label htmlFor="gapThreshold" className={styles.sliderLabel}>
                      Gain Ratio: <strong>{gainRatio}</strong>
                    </label>
                  </div>
                </Col>
                <Col xs={4} md={4}>
                  {availableDates.start && availableDates.end && (
                    <div className={styles.sliderContainer}>
                      <label htmlFor="dateRangeSlider" className={styles.sliderLabel}>
                        Selected Start Date: {new Date(dateRange.start).toLocaleDateString()}
                      </label>
                    </div>
                  )}
                </Col>
              </Row>
            </>
          )}
          <Row>
            <Col md={6}>
              <span className={styles.settingsTitle}>Configuration Settings
              </span>
            </Col>
            <Col md={6}>
              <span className={styles.settingsTitle}>News / Events</span>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Row>
                <Col md={3}
                  onClick={toggleFvgData}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    background: "#f1f1f1",
                    padding: "0.5rem",
                    borderRadius: "4px",
                  }}
                >
                  {showFvgData ? <FaMinus /> : <FaPlus />}
                  <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                    {showFvgData ? "FVG" : "FVG"}
                  </span>
                </Col>
                <Col md={5}
                  onClick={toggleLiquiditySweep}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    background: "#f1f1f1",
                    padding: "0.5rem",
                    borderRadius: "4px",
                  }}
                >
                  {showLiquiditySweep ? <FaMinus /> : <FaPlus />}
                  <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                    {showLiquiditySweep ? "Liquidity Sweep" : "Liquidity Sweep"}
                  </span>
                </Col>
                <Col md={4}
                  onClick={toggleOrderBook}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    background: "#f1f1f1",
                    padding: "0.5rem",
                    borderRadius: "4px",
                  }}
                >
                  {/* CONDITIONAL RENDER OF THE NEWS SECTION */}
                  {showOrderBook ? <FaMinus /> : <FaPlus />}
                  <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                    {showOrderBook ? "Order Book" : "Order Book"}
                  </span>
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <Row>
                <Col
                  onClick={toggleExpand}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    background: "#f1f1f1",
                    padding: "0.5rem",
                    borderRadius: "4px",
                  }}
                >
                  {/* CONDITIONAL RENDER OF THE NEWS SECTION */}
                  {expanded ? <FaMinus /> : <FaPlus />}
                  <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                    {expanded ? "News / Sentiment" : "News / Sentiment"}
                  </span>
                </Col>
                <Col
                  onClick={toggleCalendarEventExpand}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    background: "#f1f1f1",
                    padding: "0.5rem",
                    borderRadius: "4px",
                  }}
                >
                  {/* CONDITIONAL RENDER OF THE NEWS SECTION */}
                  {calendarEventExpanded ? <FaMinus /> : <FaPlus />}
                  <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                    {calendarEventExpanded ? "Calendar Events" : "Calendar Events"}
                  </span>
                </Col>
              </Row>
            </Col>
          </Row>
          <div style={{ margin: "1rem 0" }}>
            {/* Clickable header bar with an icon */}




          </div>


          {/* Conditionally render the content if expanded */}
          {expanded && (
            <>
              <div style={{ margin: "1rem 0" }}>
                {/* Clickable header bar with an icon */}
                <Row style={{ marginTop: "1rem" }}>
                  <NewsAndSentiment1 styles={styles} Row={Row} Col={Col} />
                </Row>
              </div>
            </>

          )}


          <div style={{ margin: "1rem 0" }}>
            {/* Clickable header bar with an icon */}


            {/* Conditionally render the content if expanded */}
            {calendarEventExpanded && (
              <Row style={{ marginTop: "1rem" }}>
                <CalendarEventData styles={styles} Row={Row} Col={Col} />
              </Row>
            )}
          </div>



          {/* CONDITIONAL RENDER OF THE SPECIFICATION SECTION */}
          {showFvgData && (<>
            <Row>
              <span className={styles.settingsTitle}>Failr Value Gap Settings
              </span>
            </Row>
            <Row>
              <Col xs={2} md={4}>
                <div className={styles.sliderContainer}>
                  <label htmlFor="gapThreshold" className={styles.sliderLabel}>
                    FVG Threshold: {gapThreshold.toFixed(4)}
                  </label>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={2} md={4}>
                <div className={styles.sliderContainer}>
                  <input
                    type="range"
                    id="gapThreshold"
                    min="0.0001"
                    max={maxDifference}
                    step="0.0001"
                    value={gapThreshold}
                    onChange={handleGapThresholdChange}
                    className={styles.slider}
                  />
                </div>
              </Col>
            </Row>
          </>)}

          {showLiquiditySweep && (<>
            <Row>
              <span className={styles.settingsTitle}>Liquidity Sweep Settings
              </span>
            </Row>
            <Row>
              <Col xs={2} md={8}>
                <div className={styles.sliderContainer}>
                  <label htmlFor="lsGapThreshold" className={styles.sliderLabel}>
                    Liquidity Sweep Threshold
                  </label>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={2} md={4}>
                <div className={styles.sliderContainer}>
                  <input
                    type="range"
                    id="lsGapThreshold"
                    min="0.0001"
                    max={lsMaxDifference}
                    step="0.0001"
                    value={lsGapThreshold}
                    onChange={handleLSGapThresholdChange}
                    className={styles.slider}
                  />
                </div>
              </Col>
            </Row>
            <Col xs={2} md={4}>
              <div className={styles.sliderContainer}>
                <label htmlFor="gapThreshold" className={styles.sliderLabel}>
                  LS Threshold: {lsGapThreshold.toFixed(4)}
                </label>
              </div>
            </Col>
          </>
          )}

          {/* Conditionally render the content if expanded */}
          {showOrderBook && (
            <>
              <Row>
                <span className={styles.settingsTitle}>Order Book Settings
                </span>
              </Row>
              <Row>
                <Col xs={4} md={4}>
                  <div className={styles.sliderContainer}>
                    <label htmlFor="gapThreshold" className={styles.sliderLabel}>
                      Imbalance Threshold: {orderBookImbalance}
                    </label>
                  </div>
                </Col>
                <Col xs={4} md={4}>
                  <div className={styles.sliderContainer}>
                    <label htmlFor="lossRation" className={styles.sliderLabel}>
                      Range Threshold: {orderBookRange}
                    </label>
                  </div>
                </Col>
                <Col xs={4} md={4}>
                  <div className={styles.sliderContainer}>
                    <label htmlFor="lossRation" className={styles.sliderLabel}>
                      Candlestick Range Threshold: {orderBookCandleRange}
                    </label>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={4} md={4}>
                  <div className={styles.sliderContainer}>
                    <input
                      type="range"
                      id="orderBookImbalance"
                      min="0.0"
                      max="5"
                      step="0.25"
                      value={orderBookImbalance}
                      onChange={handleOrderBookImbalanceChange}
                      className={styles.slider}
                    />
                  </div>
                </Col>
                <Col xs={4} md={4}>
                  <div className={styles.sliderContainer}>
                    <input
                      type="range"
                      id="orderBookRange"
                      min="0.0"
                      max="5"
                      step="0.25"
                      value={orderBookRange}
                      onChange={handleOrderBookRangeChange}
                      className={styles.slider}
                    />
                  </div>
                </Col>
                <Col xs={4} md={4}>
                  <div className={styles.sliderContainer}>
                    <input
                      type="range"
                      id="orderBookCandleRange"
                      min="1"
                      max="20"
                      step="1"
                      value={orderBookCandleRange}
                      onChange={handleOrderBookCandleRangeChange}
                      className={styles.slider}
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* Plotly Chart */}
          <Row>
            <Col md={12}>
              {console.log("ChartData Data:", chartData)}

              {chartData && plotTraces.length > 0 && (
                <Plot
                  data={plotTraces}
                  layout={{
                    ...chartData.layout,
                    rangeslider: {
                      visible: true,
                      bgcolor: "var(--color-bg-panel)", // Match background
                    },
                    margin: { t: 40, r: 40, b: 40, l: 40 },
                    autosize: true,
                    legend: {
                      orientation: "h",
                      x: 0,
                      y: legendFontPosition,
                      font: {
                        size: legendFontSize, // dynamically set
                      },
                    },
                    xaxis: {
                      ...chartData.layout.xaxis,
                      range: [dateRange.start, dateRange.end], // Use the date range from state
                    },
                  }}
                  onRelayout={handleRelayout} // Update date range on relayout
                  // Hook into the click event to toggle lines
                  onLegendClick={handleLegendClick}
                  config={{ responsive: true, scrollZoom: true }}
                  style={{ width: "100%", height: "65vh" }}
                />
              )}
            </Col>
          </Row>
          <CftcDataChart currencyPair={currencyPair}/>
          <CftcDataTable currencyPair={currencyPair}/>
        </Col>
        <Col md={4}>
          <NewsAndSentiment styles={styles} Row={Row} Col={Col} />
          
        </Col>
      </Row>


    </Container>
  );
};

export default FVGChart;
